import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import apiService from '../services/apiService';

function userCanViewMargin() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return ['super_admin', 'admin', 'manager'].includes(u?.role);
  } catch {
    return false;
  }
}

export default function JobCostingPanel({ collectionId, open }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [priceBook, setPriceBook] = useState([]);
  const [busy, setBusy] = useState(false);
  const [laborForm, setLaborForm] = useState({ worker_label: '', hours: '', hourly_rate: '' });
  const [costForm, setCostForm] = useState({ description: '', quantity: '1', unit_cost: '', price_book_item_id: '' });
  const [settings, setSettings] = useState({ price_book_item_id: '', default_hourly_rate: '', sell_price: '' });

  const canViewMargin = userCanViewMargin();

  const load = useCallback(async () => {
    if (!collectionId || !open) return;
    setLoading(true);
    setError('');
    try {
      const [costing, pb] = await Promise.all([
        apiService.get(`collections/${collectionId}/job-costing`),
        apiService.get('price-book?active=1'),
      ]);
      setData(costing);
      setPriceBook(Array.isArray(pb) ? pb : []);
      setSettings({
        price_book_item_id: costing.price_book_item_id || '',
        default_hourly_rate: costing.default_hourly_rate ?? '',
        sell_price: costing.sell_amount ?? '',
      });
    } catch (e) {
      setError(e.message || 'Failed to load costing');
    } finally {
      setLoading(false);
    }
  }, [collectionId, open]);

  useEffect(() => {
    load();
  }, [load]);

  async function saveSettings() {
    setBusy(true);
    try {
      const res = await apiService.patch(`collections/${collectionId}/job-costing/settings`, {
        price_book_item_id: settings.price_book_item_id ? Number(settings.price_book_item_id) : null,
        default_hourly_rate: settings.default_hourly_rate === '' ? null : Number(settings.default_hourly_rate),
        sell_price: settings.sell_price === '' ? null : Number(settings.sell_price),
      });
      setData(res);
    } catch (e) {
      setError(e.message || 'Failed to save settings');
    } finally {
      setBusy(false);
    }
  }

  async function addLabor(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await apiService.post(`collections/${collectionId}/job-costing/labor`, {
        worker_label: laborForm.worker_label || null,
        hours: Number(laborForm.hours),
        hourly_rate: laborForm.hourly_rate === '' ? undefined : Number(laborForm.hourly_rate),
      });
      setData(res);
      setLaborForm({ worker_label: '', hours: '', hourly_rate: '' });
    } catch (err) {
      setError(err.message || 'Failed to add labor');
    } finally {
      setBusy(false);
    }
  }

  async function addCostItem(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await apiService.post(`collections/${collectionId}/job-costing/cost-items`, {
        description: costForm.description,
        quantity: Number(costForm.quantity) || 1,
        unit_cost: costForm.unit_cost === '' ? undefined : Number(costForm.unit_cost),
        price_book_item_id: costForm.price_book_item_id ? Number(costForm.price_book_item_id) : null,
      });
      setData(res);
      setCostForm({ description: '', quantity: '1', unit_cost: '', price_book_item_id: '' });
    } catch (err) {
      setError(err.message || 'Failed to add cost item');
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Job costing
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}

      {data && canViewMargin && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip label={`Sell: ${data.sell_amount ?? 0} ${data.currency || ''}`} color="primary" variant="outlined" />
          <Chip label={`Cost: ${data.total_cost ?? 0}`} variant="outlined" />
          <Chip
            label={`Margin: ${data.margin ?? 0} (${data.margin_percent ?? 0}%)`}
            color={(data.margin ?? 0) >= 0 ? 'success' : 'error'}
            variant="outlined"
          />
          <Chip size="small" label={`Sell: ${data.sell_source}`} />
        </Box>
      )}

      {data && !canViewMargin && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Total labor hours: {data.labor_hours_total ?? 0} (margin visible to managers only)
        </Typography>
      )}

      {data?.can_manage ? (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Sell & defaults
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1, mb: 2 }}>
            <TextField
              select
              size="small"
              label="Price book (sell)"
              value={settings.price_book_item_id}
              onChange={(e) => setSettings((s) => ({ ...s, price_book_item_id: e.target.value }))}
            >
              <MenuItem value="">— None —</MenuItem>
              {priceBook.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name} ({p.unit_price})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Sell override"
              type="number"
              value={settings.sell_price}
              onChange={(e) => setSettings((s) => ({ ...s, sell_price: e.target.value }))}
            />
            <TextField
              size="small"
              label="Default hourly rate"
              type="number"
              value={settings.default_hourly_rate}
              onChange={(e) => setSettings((s) => ({ ...s, default_hourly_rate: e.target.value }))}
            />
          </Box>
          <Button size="small" variant="outlined" disabled={busy} onClick={saveSettings} sx={{ mb: 2, textTransform: 'none' }}>
            Save costing settings
          </Button>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Labor
          </Typography>
          {(data.labor_entries || []).map((row) => (
            <Box key={row.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {row.worker_label || 'Labor'}: {row.hours}h
                {canViewMargin ? ` @ ${row.hourly_rate} = ${row.line_cost}` : ''}
              </Typography>
              <IconButton
                size="small"
                onClick={async () => {
                  const res = await apiService.delete(`collections/${collectionId}/job-costing/labor/${row.id}`);
                  setData(res);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Box component="form" onSubmit={addLabor} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <TextField size="small" label="Worker" value={laborForm.worker_label} onChange={(e) => setLaborForm((f) => ({ ...f, worker_label: e.target.value }))} />
            <TextField size="small" label="Hours" type="number" required value={laborForm.hours} onChange={(e) => setLaborForm((f) => ({ ...f, hours: e.target.value }))} />
            <TextField size="small" label="Rate/h" type="number" value={laborForm.hourly_rate} onChange={(e) => setLaborForm((f) => ({ ...f, hourly_rate: e.target.value }))} />
            <Button type="submit" size="small" variant="contained" disabled={busy} sx={{ textTransform: 'none' }}>
              Add labor
            </Button>
          </Box>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Materials / costs
          </Typography>
          {(data.cost_items || []).map((row) => (
            <Box key={row.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {row.description} × {row.quantity}
                {canViewMargin ? ` = ${row.amount}` : ''}
              </Typography>
              <IconButton
                size="small"
                onClick={async () => {
                  const res = await apiService.delete(`collections/${collectionId}/job-costing/cost-items/${row.id}`);
                  setData(res);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Box component="form" onSubmit={addCostItem} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <TextField
              select
              size="small"
              label="From price book"
              value={costForm.price_book_item_id}
              onChange={(e) => {
                const id = e.target.value;
                const pb = priceBook.find((p) => String(p.id) === String(id));
                setCostForm((f) => ({
                  ...f,
                  price_book_item_id: id,
                  description: pb?.name || f.description,
                  unit_cost: pb?.unit_cost ?? f.unit_cost,
                }));
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Custom</MenuItem>
              {priceBook.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField size="small" label="Description" value={costForm.description} onChange={(e) => setCostForm((f) => ({ ...f, description: e.target.value }))} />
            <TextField size="small" label="Qty" type="number" value={costForm.quantity} onChange={(e) => setCostForm((f) => ({ ...f, quantity: e.target.value }))} />
            <TextField size="small" label="Unit cost" type="number" value={costForm.unit_cost} onChange={(e) => setCostForm((f) => ({ ...f, unit_cost: e.target.value }))} />
            <Button type="submit" size="small" variant="contained" disabled={busy} sx={{ textTransform: 'none' }}>
              Add cost
            </Button>
          </Box>
        </>
      ) : null}
    </Box>
  );
}
